import { useState, useEffect, useCallback } from 'react'
import {
  ShieldAlert,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  AlertTriangle,
  RefreshCw,
  Settings,
  Loader2,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { riskApi } from '../../lib/api'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

interface Alert {
  id: string
  user_id: string
  user_name?: string
  userName?: string
  user_phone?: string
  userPhone?: string
  alert_type?: string
  alertType?: string
  description?: string
  status: string
  reviewed_by?: string
  reviewedBy?: string
  review_note?: string
  created_at?: string
  createdAt?: string
  reviewed_at?: string
  reviewedAt?: string
}

const RiskControl = () => {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [showModal, setShowModal] = useState(false)
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null)
  const [showRulesModal, setShowRulesModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successText, setSuccessText] = useState('')
  const [rules, setRules] = useState({
    frequencyThreshold: 50,
    amountThreshold: 1000,
    multiHospitalLimit: 3
  })
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const [jumpInput, setJumpInput] = useState('')

  const loadAlerts = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    try {
      const params: any = { page: currentPage, page_size: pageSize }
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter
      if (typeFilter && typeFilter !== 'all') params.alert_type = typeFilter

      const res: any = await riskApi.list(params)
      const alertList = res.alerts || res.items || []
      setAlerts(alertList)
      setTotal(res.total || 0)
    } catch {
      setAlerts([])
      setTotal(0)
    } finally {
      setTimeout(() => {
        setLoading(false)
        setRefreshing(false)
      }, 300)
    }
  }, [currentPage, pageSize, statusFilter, typeFilter])

  useEffect(() => {
    loadAlerts()
  }, [loadAlerts])

  // 筛选改变时回到第 1 页
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, typeFilter])

  // 页码按钮生成
  const getPageNumbers = (): (number | string)[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages: (number | string)[] = [1]
    if (currentPage > 3) pages.push('...')
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  const handleJump = () => {
    const p = parseInt(jumpInput)
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      setCurrentPage(p)
      setJumpInput('')
    } else {
      setJumpInput('')
    }
  }

  // ───── 静态统计：拉所有页（仅在筛选切换时触发一次） ─────
  const [stats, setStats] = useState({ all: 0, pending: 0, frequency: 0, amount: 0, multi_hospital: 0 })

  useEffect(() => {
    let aborted = false
    const loadStats = async () => {
      try {
        // 1. 总数
        const res1: any = await riskApi.list({ page: 1, page_size: 1 })
        if (aborted) return
        const totalAll = res1.total || 0

        // 2. 各类别（拉全量用于统计；告警数一般不大）
        const allAlerts: Alert[] = []
        let page = 1
        const limit = 100
        while (true) {
          const res: any = await riskApi.list({ page, page_size: limit })
          const list = res.alerts || res.items || []
          allAlerts.push(...list)
          if (list.length < limit || allAlerts.length >= totalAll) break
          page++
        }
        if (aborted) return
        setStats({
          all: totalAll,
          pending: allAlerts.filter(a => a.status === 'pending').length,
          frequency: allAlerts.filter(a => (a.alert_type || a.alertType) === 'frequency').length,
          amount: allAlerts.filter(a => (a.alert_type || a.alertType) === 'amount').length,
          multi_hospital: allAlerts.filter(a => (a.alert_type || a.alertType) === 'multi_hospital').length
        })
      } catch {
        if (!aborted) setStats({ all: 0, pending: 0, frequency: 0, amount: 0, multi_hospital: 0 })
      }
    }
    // 仅在筛选变化时统计，页码变化不重新统计
    loadStats()
    return () => { aborted = true }
  }, [statusFilter, typeFilter])

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'frequency': return '高频异常'
      case 'amount': return '积分异常'
      case 'multi_hospital': return '多机构异常'
      default: return type
    }
  }

  const getAlertTypeColor = (type: string) => {
    switch (type) {
      case 'frequency': return 'bg-orange-100 text-orange-700'
      case 'amount': return 'bg-red-100 text-red-700'
      case 'multi_hospital': return 'bg-purple-100 text-purple-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending': return { label: '待处理', icon: Clock, color: 'text-orange-600', bg: 'bg-orange-100' }
      case 'reviewed': return { label: '已审核', icon: Eye, color: 'text-blue-600', bg: 'bg-blue-100' }
      case 'resolved': return { label: '已解决', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' }
      default: return { label: status, icon: Clock, color: 'text-gray-600', bg: 'bg-gray-100' }
    }
  }

  const pendingCount = stats.pending

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">风控中心</h1>
          <p className="text-gray-500 text-sm mt-1">监控和处理异常积分行为</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowRulesModal(true)}
            className="flex items-center space-x-2 bg-white border border-gray-200 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span>风控规则</span>
          </button>
          <button
            onClick={() => loadAlerts(true)}
            disabled={refreshing}
            className="flex items-center space-x-2 bg-white border border-gray-200 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? '刷新中...' : '刷新数据'}</span>
          </button>
        </div>
      </div>

      {/* 预警统计 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => { setStatusFilter('pending'); setTypeFilter('all'); }}
          className={`bg-white rounded-2xl shadow-md p-6 text-left hover:shadow-lg transition-shadow ${statusFilter === 'pending' ? 'ring-2 ring-orange-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">待处理告警</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{pendingCount}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </button>
        <button
          onClick={() => { setStatusFilter('all'); setTypeFilter('frequency'); }}
          className={`bg-white rounded-2xl shadow-md p-6 text-left hover:shadow-lg transition-shadow ${typeFilter === 'frequency' ? 'ring-2 ring-orange-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">高频异常</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{stats.frequency}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-xl">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </button>
        <button
          onClick={() => { setStatusFilter('all'); setTypeFilter('amount'); }}
          className={`bg-white rounded-2xl shadow-md p-6 text-left hover:shadow-lg transition-shadow ${typeFilter === 'amount' ? 'ring-2 ring-red-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">积分异常</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{stats.amount}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-xl">
              <ShieldAlert className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </button>
        <button
          onClick={() => { setStatusFilter('all'); setTypeFilter('multi_hospital'); }}
          className={`bg-white rounded-2xl shadow-md p-6 text-left hover:shadow-lg transition-shadow ${typeFilter === 'multi_hospital' ? 'ring-2 ring-purple-500' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">多机构异常</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{stats.multi_hospital}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-xl">
              <XCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </button>
      </div>

      {/* 筛选 */}
      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">全部状态</option>
              <option value="pending">待处理</option>
              <option value="reviewed">已审核</option>
              <option value="resolved">已解决</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">全部类型</option>
              <option value="frequency">高频异常</option>
              <option value="amount">积分异常</option>
              <option value="multi_hospital">多机构异常</option>
            </select>
          </div>
        </div>
      </div>

      {/* 告警列表 */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center text-gray-400">暂无告警记录</div>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => {
            const statusConfig = getStatusConfig(alert.status)
            const StatusIcon = statusConfig.icon
            return (
              <div
                key={alert.id}
                className={`bg-white rounded-2xl shadow-md p-6 transition-all ${
                  alert.status === 'pending' ? 'border-l-4 border-orange-400' : ''
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-xl ${statusConfig.bg}`}>
                      <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-gray-800">{alert.user_name || alert.userName || ''}</h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getAlertTypeColor(alert.alert_type || alert.alertType || '')}`}>
                          {getAlertTypeLabel(alert.alert_type || alert.alertType || '')}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">{alert.description || ''}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>手机: {alert.user_phone || alert.userPhone || ''}</span>
                        <span>告警时间: {alert.created_at || alert.createdAt || ''}</span>
                      </div>
                      {alert.review_note && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">处理备注:</span> {alert.review_note}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            审核人: {alert.reviewed_by || alert.reviewedBy || ''} | {alert.reviewed_at || alert.reviewedAt || ''}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {alert.status === 'pending' && (
                      <button
                        onClick={() => { setSelectedAlert(alert); setShowModal(true); }}
                        className="px-4 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors"
                      >
                        处理
                      </button>
                    )}
                    <button
                      onClick={() => { setSelectedAlert(alert); setShowModal(true); }}
                      className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      查看详情
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* 分页 */}
      {!loading && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* 左侧：统计 + 每页条数 */}
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>共 {total} 条</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
            >
              {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} 条/页</option>)}
            </select>
          </div>

          {/* 右侧：页码 + 跳转 */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {getPageNumbers().map((p, i) =>
                p === '...' ? (
                  <span key={`e-${i}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p as number)}
                    className={`min-w-[36px] px-2 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === p
                        ? 'bg-green-700 text-white'
                        : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
              <div className="flex items-center gap-1 ml-2">
                <span className="text-sm text-gray-400">跳至</span>
                <input
                  type="text"
                  value={jumpInput}
                  onChange={(e) => setJumpInput(e.target.value.replace(/[^0-9]/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                  onBlur={handleJump}
                  placeholder={String(currentPage)}
                  className="w-12 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-green-500"
                />
                <span className="text-sm text-gray-400">页</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 详情/处理模态框 */}
      {showModal && selectedAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">
                {selectedAlert.status === 'pending' ? '处理告警' : '告警详情'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">用户姓名</p>
                    <p className="font-medium text-gray-800">{selectedAlert.user_name || selectedAlert.userName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">手机号</p>
                    <p className="font-medium text-gray-800">{selectedAlert.user_phone || selectedAlert.userPhone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">告警类型</p>
                    <p className="font-medium text-gray-800">
                      {getAlertTypeLabel(selectedAlert.alert_type || selectedAlert.alertType || '')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">告警时间</p>
                    <p className="font-medium text-gray-800">{selectedAlert.created_at || selectedAlert.createdAt}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">状态</p>
                    <p className="font-medium text-gray-800">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusConfig(selectedAlert.status).bg} ${getStatusConfig(selectedAlert.status).color}`}>
                        {getStatusConfig(selectedAlert.status).label}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">告警描述</p>
                <p className="text-gray-800">{selectedAlert.description}</p>
              </div>

              {selectedAlert.status === 'pending' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">处理方式</label>
                    <div className="space-y-2">
                      <label className="flex items-center p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                        <input type="radio" name="action" value="normal" className="w-4 h-4 text-green-600" />
                        <span className="ml-3 text-gray-700">标记为正常（不处理）</span>
                      </label>
                      <label className="flex items-center p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                        <input type="radio" name="action" value="deduct" className="w-4 h-4 text-orange-600" />
                        <span className="ml-3 text-orange-700 font-medium">扣除相关积分</span>
                      </label>
                      <label className="flex items-center p-3 border border-red-200 rounded-xl cursor-pointer hover:bg-red-50 transition-colors bg-red-50">
                        <input type="radio" name="action" value="freeze" className="w-4 h-4 text-red-600" />
                        <span className="ml-3 text-red-700 font-medium">冻结用户账户</span>
                        <AlertTriangle className="w-4 h-4 text-red-500 ml-2" />
                      </label>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mt-3">
                      <p className="text-xs text-yellow-700 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>注意：扣除积分和冻结账户为不可逆操作，请谨慎处理</span>
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">处理备注</label>
                    <textarea
                      id="review-note"
                      rows={3}
                      placeholder="请输入处理备注..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    />
                  </div>
                </>
              ) : (
                selectedAlert.review_note && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">处理备注:</span> {selectedAlert.review_note}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      审核人: {selectedAlert.reviewed_by || ''} | {selectedAlert.reviewed_at || ''}
                    </p>
                  </div>
                )
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                {selectedAlert.status === 'pending' ? '取消' : '关闭'}
              </button>
              {selectedAlert.status === 'pending' && (
                <button
                  onClick={async () => {
                    const action = (document.querySelector('input[name="action"]:checked') as HTMLInputElement)?.value
                    const note = (document.getElementById('review-note') as HTMLTextAreaElement)?.value
                    if (!action) {
                      alert('请选择处理方式')
                      return
                    }
                    try {
                      await riskApi.review(selectedAlert.id, action as 'normal' | 'deduct' | 'freeze', note)
                      setShowModal(false)
                      loadAlerts()
                    } catch {
                      alert('处理失败，请重试')
                    }
                  }}
                  className="px-6 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors"
                >
                  确认处理
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 风控规则配置弹窗 */}
      {showRulesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">风控规则配置</h3>
                <p className="text-green-100 text-sm">设置异常检测阈值</p>
              </div>
              <button onClick={() => setShowRulesModal(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">单日积分获取上限</label>
                <input type="number" value={rules.frequencyThreshold}
                  onChange={(e) => setRules({ ...rules, frequencyThreshold: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                <p className="text-xs text-gray-400 mt-1">超过此分数将触发高频异常告警</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">单次积分获取上限</label>
                <input type="number" value={rules.amountThreshold}
                  onChange={(e) => setRules({ ...rules, amountThreshold: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                <p className="text-xs text-gray-400 mt-1">单次操作超过此分数将触发积分异常告警</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">多机构操作限制</label>
                <input type="number" value={rules.multiHospitalLimit}
                  onChange={(e) => setRules({ ...rules, multiHospitalLimit: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                <p className="text-xs text-gray-400 mt-1">同一用户跨机构操作超过此次数将触发多机构异常</p>
              </div>
            </div>
            <div className="p-6 border-t flex space-x-3">
              <button onClick={() => setShowRulesModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors">
                取消
              </button>
              <button onClick={() => { setShowRulesModal(false); setSuccessText('规则配置已保存！'); setShowSuccessModal(true) }}
                className="flex-1 py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors">
                保存配置
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 成功提示弹窗 */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{successText}</h3>
              <button onClick={() => setShowSuccessModal(false)}
                className="w-full py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors">
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RiskControl
